import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import type { Plan, PlanCode, PlanCodePage } from "../types";
import { useToast } from "../toast";
import { fmtDate } from "../format";

interface Props {
  open: boolean;
  plan: Plan | null;
  onClose: () => void;
}

export default function PlanCodesDialog({ open, plan, onClose }: Props) {
  const api = useAuthApi();
  const [codes, setCodes] = useState<PlanCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [manualInput, setManualInput] = useState("");
  const [generateCount, setGenerateCount] = useState("10");
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const toast = useToast();

  const load = useCallback(async (nextPage = page, nextRowsPerPage = rowsPerPage) => {
    if (!plan) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(nextRowsPerPage),
        offset: String(nextPage * nextRowsPerPage),
      });
      const data = await api<PlanCodePage>(`${MGMT}/plans/${plan.id}/codes?${params.toString()}`);
      setCodes(data?.items ?? []);
      setTotal(data?.count ?? data?.total ?? 0);
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }, [api, page, plan, rowsPerPage, toast]);

  useEffect(() => {
    if (open) {
      setCodes([]);
      setManualInput("");
      setGenerateCount("10");
      setTotal(0);
      setPage(0);
      setRowsPerPage(25);
    }
  }, [open, plan?.id]);

  useEffect(() => {
    if (open && plan) {
      void load();
    }
  }, [load, open, plan]);

  const handleAddManual = async () => {
    const rawCodes = manualInput.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (rawCodes.length === 0) return;
    setSaving(true);
    try {
      await api(`${MGMT}/plans/${plan!.id}/codes`, {
        method: "POST",
        body: JSON.stringify({ codes: rawCodes }),
      });
      toast.show(`${rawCodes.length} code(s) added`);
      setManualInput("");
      await load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    const n = parseInt(generateCount, 10);
    if (!n || n < 1) return;
    setSaving(true);
    try {
      await api(`${MGMT}/plans/${plan!.id}/codes`, {
        method: "POST",
        body: JSON.stringify({ generate: n }),
      });
      toast.show(`${n} code(s) generated`);
      await load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (codeId: number) => {
    try {
      await api(`${MGMT}/plans/${plan!.id}/codes/${codeId}`, { method: "DELETE" });
      toast.show("Code deleted");
      if (codes.length === 1 && page > 0) {
        setPage(page - 1);
      } else {
        await load();
      }
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  const usedCount = codes.filter((c) => c.is_used).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Access Codes — {plan?.name}
        <Typography variant="body2" color="text.secondary">
          {total} total · {codes.length} shown · {usedCount} used on page
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {/* Add manually */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>Add codes manually</Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                fullWidth
                placeholder="Enter codes separated by commas or spaces"
                value={manualInput}
                onChange={(e) => setManualInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddManual()}
              />
              <Button variant="outlined" onClick={handleAddManual} disabled={saving || !manualInput.trim()}>
                Add
              </Button>
            </Stack>
          </Box>

          <Divider>
            <Typography variant="caption" color="text.secondary">or</Typography>
          </Divider>

          {/* Generate random */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>Generate random codes</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                type="number"
                label="Count"
                value={generateCount}
                onChange={(e) => setGenerateCount(e.target.value)}
                InputProps={{ inputProps: { min: 1, max: 500 } }}
                sx={{ width: 120 }}
              />
              <Button variant="outlined" onClick={handleGenerate} disabled={saving}>
                Generate
              </Button>
            </Stack>
          </Box>

          <Divider />

          {/* Code list */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2">Code list</Typography>
            <IconButton size="small" onClick={() => void load()} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Stack>

          {loading ? (
            <Box sx={{ textAlign: "center", py: 2 }}><CircularProgress size={24} /></Box>
          ) : codes.length === 0 ? (
            <Typography color="text.secondary" variant="body2">No codes yet.</Typography>
          ) : (
            <Paper variant="outlined">
              <TableContainer sx={{ maxHeight: 320 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Used by Contract</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {codes.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>{c.code}</TableCell>
                        <TableCell>
                          <Chip
                            label={c.is_used ? "Used" : "Available"}
                            color={c.is_used ? "default" : "success"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {c.used_by_contract_id ? `#${c.used_by_contract_id}` : "—"}
                        </TableCell>
                        <TableCell sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
                          {fmtDate(c.created_at)}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={c.is_used ? "Cannot delete used code" : "Delete"}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={c.is_used}
                                onClick={() => handleDelete(c.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_event, nextPage) => setPage(nextPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </Paper>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
